import React, { useState } from "react";
import axios from "axios";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { useNavigate, useLocation } from "react-router-dom";
import Loader from "../common/Loader";
import { API_BASE_URL } from "../config/constants";
import { X } from "lucide-react";

const Login = ({ BASEURL }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const location = useLocation();
  const successMessage = location.state?.message;
  const [fieldError, setFieldError] = useState({
    email: "",
    password: "",
  });

  const getDeviceId = async () => {
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    return result.visitorId;
  };


  const handleForgotPassword = () => {

  }

  const handleLogin = () => {
    setLoading(true);
    setFieldError({ email: "", password: "" });

    getDeviceId()
      .then((deviceId) => {
        // save deviceId for later
        localStorage.setItem("deviceId", deviceId);

        return axios.post(`${API_BASE_URL}/login`, {
          email,
          password,
          deviceId, // send to backend
        });
      })

      .then((response) => {
        const { token } = response.data || {};

        if (!token) {
          throw new Error("Login failed");
        }

        localStorage.setItem("token", token);
        localStorage.setItem("state", JSON.stringify(response.data));

        navigate("/dashboard", { replace: true });
      })
      .catch((err) => {
        if (axios.isAxiosError(err)) {
          const message =
            err.response?.data?.message || "Invalid email or password";

          // 👇 apply error to both fields
          setFieldError({
            email: "Invalid email",
            password: "Incorrect password",
          });
        } else {
          setFieldError({
            email: "Something went wrong",
            password: "Something went wrong",
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };


  return (

    <div className="min-h-screen bg-[linear-gradient(180deg,#073631_0%,#07201D_100%)] flex flex-col justify-center items-center px-4 py-8 overflow-hidden">
      {/* Error Alert */}
      {successMessage && (
        <div className="mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg text-sm">
          {successMessage}
        </div>
      )}

      {error && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 max-w-sm w-full px-4 z-50">
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center justify-between text-sm">
            <div>
              <strong className="font-bold">Error! </strong>
              {error}
            </div>
            <button onClick={() => setError(null)} className="text-red-700 hover:text-red-900">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Login Card */}
      <div className="w-full max-w-[38rem]
  h-auto
  xl:min-h-[46.625rem]
  bg-[#051E1C]
  backdrop-blur-sm
  rounded-2xl
  shadow-2xl
  px-[5.0625rem]
  pt-[5.3125rem]
  pb-[4.25rem]">
        {/* Logo */}
        <div className="flex mb-[4.125rem]">
          <img src="assets/new-kirloskar.svg" alt="Logo" className="w-[16.6159rem] h-[3.125rem]" />
        </div>

        {/* Title */}
        <h2 className="text-[2.25rem] font-poppins font-semibold text-white mb-8">
          Login
        </h2>


        {/* Form */}

        {/* Username */}

        <div className="mb-[1.25rem]">
          <label className="block text-[#D9D9D9] text-[1.125rem] font-normal mb-1.5">
            Username*
          </label>

          <input
            type="email"
            placeholder="Enter your name"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setFieldError((prev) => ({ ...prev, email: "" }));
            }}
            className={`w-[27.3125rem] h-[3.4375rem] py-[0.625rem] px-[0.875rem] 
    bg-[#33504D75] text-white rounded-lg text-[1.125rem]
    ${fieldError.email
                ? "border border-red-500 focus:ring-red-500/40"
                : "focus:border-gray-300 focus:ring-teal-500/50"
              }`}
          />



          <div className="min-h-[1.25rem] mt-1">
            <p
              className={`text-sm ${fieldError.email ? "text-red-500" : "invisible"
                }`}
            >
              {fieldError.email || "placeholder"}
            </p>
          </div>
        </div>

        {/* Password */}
        <div className="mb-[3.5625rem]">
          {/* Password Label */}
          <label className="block text-[#D9D9D9] text-[1.125rem] font-normal mb-1.5">
            Password*
          </label>

          {/* Input Field */}
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Enter Password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setFieldError((prev) => ({ ...prev, password: "" }));
              }}
              className={`w-[27.3125rem] h-[3.4375rem] py-[0.625rem] px-[0.875rem] bg-[#33504D75] text-white rounded-lg text-[1.125rem] pr-11
      ${fieldError.password
                  ? "border border-red-500 focus:ring-red-500/40"
                  : "focus:border-gray-300 focus:ring-teal-500/50"
                }`}
            />

            {/* Toggle Eye */}
            <div
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-[2.75rem] flex items-center cursor-pointer"
            >
              <img
                src={showPassword ? "assets/eye-open.svg" : "assets/eye-off.svg"}
                alt="toggle password visibility"
                className="h-5 w-5 invert"
              />
            </div>
          </div>

          {/* Forgot Password immediately below input */}
          <div className="mt-1 flex justify-between items-center">
            {/* Error message on the left */}
            <p
              className={`text-sm ${fieldError.password ? "text-red-500" : "invisible"
                }`}
            >
              {fieldError.password || "placeholder"}
            </p>

            {/* Forgot Password link on the right */}
            <span
              onClick={() => navigate("/forgot-password")}
              className="text-[1.125rem] text-[#D9D9D9] cursor-pointer mr-[1.5625rem] mt-[0.5rem]"
            >
              Forgot Password?
            </span>
          </div>
        </div>

        {/* Login Button */}
        <button
          onClick={handleLogin}
          disabled={loading}
          className="w-[27.3125rem] bg-[#147A6F] px-[0.625rem]
 mb-[2.8125rem] text-white font-medium py-[1.125rem]
 rounded-[6.25rem] text-[1.375rem]"
        >
          {loading ? "Logging in..." : "Login"}
        </button>

        {/* Reset Password */}
        <div className="text-center">
          <button
            onClick={() => {
              if (!email) {
                setFieldError((prev) => ({
                  ...prev,
                  email: "Please enter the email first",
                }));
                return;
              }

              navigate("/reset-password", { state: { email } });
            }}
            disabled={loading}
            className="text-[#D9D9D9] text-[1.25rem] transition underline-offset-2 cursor-pointer"
          >
            Reset Password
          </button>
        </div>
      </div>
    </div>

  );
};

export default Login;